/** Shared JSON body shape for POST /tournament/create and fields blob for PUT /updateTournament */

export function mapBallTypeForApi(bt?: string | null): string {
  if (!bt) return "Leather";
  if (bt === "BallType.leather") return "Leather";
  if (bt === "BallType.tennis") return "Tennis";
  if (bt === "BallType.other") return "Other";
  return bt;
}

/** UI match type → PUT /updateTournament `matchType` enum (TEST → TEST_MATCH). */
export function mapMatchTypeForUpdateApi(ui: string): string {
  const u = (ui || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (u === "TEST") return "TEST_MATCH";
  return u;
}

function normalizeCountryCodeForApi(code: unknown): string {
  const s = code != null ? String(code).trim() : "";
  if (!s) return "+91";
  return s.startsWith("+") ? s : `+${s.replace(/^\+/, "")}`;
}

function readBool(draft: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = draft[k];
    if (typeof v === "boolean") return v;
  }
  return undefined;
}

function readNumber(draft: Record<string, unknown>, key: string, fallback: number): number {
  const v = draft[key];
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return fallback;
}

/**
 * PhoneInput's value carries the dial code (e.g. "919876543210"); the backend
 * and the mobile app store the national number and the country code separately.
 */
export function toNationalPhoneNumber(phone: unknown, countryCode: unknown): string {
  const digits = phone != null ? String(phone).replace(/\D/g, "") : "";
  const cc = countryCode != null ? String(countryCode).replace(/\D/g, "") : "";
  return cc && digits.startsWith(cc) && digits.length > cc.length ? digits.slice(cc.length) : digits;
}

/**
 * The multipart endpoints parse `tournamentRules` with a regex expecting the
 * unquoted `{key: value, list: [a, b]}` form the mobile app sends
 * (PutTournamentRequest.parseTournamentRules). Quoted JSON keys don't match
 * and every rule is silently dropped, so serialize to that form instead.
 */
export function serializeTournamentRules(rules: unknown): string {
  const r = (rules ?? {}) as Record<string, unknown>;
  const list = (v: unknown) => (Array.isArray(v) ? `[${v.join(", ")}]` : "[]");
  return (
    `{countWideBall: ${!!r.countWideBall}, runsForWideBall: ${Number(r.runsForWideBall ?? 1)}, ` +
    `countNoBall: ${!!r.countNoBall}, runsForNoBall: ${Number(r.runsForNoBall ?? 1)}, ` +
    `excludeRules: ${list(r.excludeRules)}, excludeOvers: ${list(r.excludeOvers)}}`
  );
}

function tournamentRulesForApi(draft: Record<string, unknown>, isTest: boolean): string {
  const rules = (draft?.tournamentRules as Record<string, unknown>) ?? {};
  return serializeTournamentRules(isTest ? { ...rules, excludeRules: [], excludeOvers: [] } : rules);
}

/** A cropped image held as raw base64 (no data: prefix) until submit. */
export interface TournamentImagePart {
  base64: string;
  fileType: string;
  fileName: string;
}

/**
 * Appends a `bannerUri` / `logoUri` file part only when an image was picked.
 * Both are optional on the backend — never send an empty placeholder part,
 * or it gets stored as a 0-byte image.
 */
export function appendTournamentImagePart(
  formData: FormData,
  partName: "bannerUri" | "logoUri",
  image: TournamentImagePart | null | undefined,
) {
  if (!image?.base64) return;
  const ext = image.fileType || "png";
  const mime = ext.includes("/") ? ext : `image/${ext}`;
  const byteString = atob(image.base64);
  const buf = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) buf[i] = byteString.charCodeAt(i);
  formData.append(partName, new Blob([buf], { type: mime }), image.fileName || `${partName}.${ext}`);
}

/** Flattens a payload into multipart fields; nulls are skipped (the backend treats absent as unset). */
export function appendTournamentFields(formData: FormData, fields: Record<string, unknown>) {
  for (const [key, value] of Object.entries(fields)) {
    if (value === null || value === undefined) continue;
    formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  }
}

/** Multipart fields for POST /tournament (images are appended separately). */
export function buildCreateTournamentPayload(draft: Record<string, unknown>, selectedLeagueId: string | null) {
  const isLimitedOvers = draft?.matchType === "LIMITED_OVERS";
  const isTest = draft?.matchType === "TEST";
  const countryCode = draft?.organizerPhoneNumberCountryCode
    ? String(draft.organizerPhoneNumberCountryCode).startsWith("+")
      ? draft.organizerPhoneNumberCountryCode
      : `+${draft.organizerPhoneNumberCountryCode}`
    : "+91";

  return {
    tournamentName: draft?.tournamentName ?? "",
    description: draft?.description ?? "",
    organizerName: draft?.organizerName ?? "",
    organizerPhoneNumber: toNationalPhoneNumber(draft?.organizerPhoneNumber, countryCode),
    organizerPhoneNumberCountryCode: countryCode,
    organizerEmailId: draft?.organizerEmailId ?? "",
    startDate: draft?.startDate ?? "",
    endDate: draft?.endDate ?? "",
    matchType: mapMatchTypeForUpdateApi(String(draft?.matchType ?? "LIMITED_OVERS")),
    ballType: mapBallTypeForApi(draft?.ballType as string | null),
    tournamentCategory: draft?.tournamentCategory ?? "OTHER",
    leagueId: selectedLeagueId ?? draft?.leagueId ?? "",
    maxOversPerBowler: isTest ? 0 : (draft?.maxOversPerBowler ?? 0),
    maxOvers: isLimitedOvers ? (draft?.maxOvers ?? 0) : 0,
    maxWickets: isLimitedOvers ? (draft?.maxWickets ?? 0) : 0,
    maxPowerPlayOvers: typeof draft?.maxPowerPlayOvers === "number" ? draft.maxPowerPlayOvers : 0,
    pointForWin: isTest ? (draft?.pointForOutrightWin ?? 0) : (draft?.pointForWin ?? 0),
    pointForTie: draft?.pointForTie ?? 0,
    pointForAbandoned: draft?.pointForAbandoned ?? 0,
    isRosterLocked: !!draft?.isRosterLocked,
    canEditScoreCard: !!draft?.canEditScoreCard,
    isImpactPlayerAllowed: !!draft?.isImpactPlayerAllowed,
    isAbandonedGameCounted:
      typeof draft?.isAbandonedGameCounted === "boolean" ? draft.isAbandonedGameCounted : true,
    rankingPreference: (draft?.rankingPreference as string) || "TOTAL_POINTS",
    scheduledDays: typeof draft?.scheduledDays === "number" ? draft.scheduledDays : 0,
    isTickerAutomationEnabled: !!draft?.isTickerAutomationEnabled,
    bonusPointsEnabled: !!draft?.bonusPointsEnabled,
    isStrictModeEnabled: !!draft?.isStrictModeEnabled,
    isPowerPlayEnabled: !!draft?.isPowerPlayEnabled,
    waiverRequired: !!draft?.waiverRequired,
    waiverId: draft?.waiverId ?? null,
    tournamentRules: tournamentRulesForApi(draft, isTest),
    ...(isTest ? {
      pointForFirstInningsLead: draft?.pointForFirstInningsLead ?? null,
      pointForFirstInningsLoss: draft?.pointForFirstInningsLoss ?? null,
      pointForOutrightWin: draft?.pointForOutrightWin ?? null,
      bonusPointForWinByInnings: draft?.bonusPointForWinByInnings ?? null,
    } : {}),
  };
}

/**
 * Fields JSON for PUT /updateTournament (query `fields`).
 * Matches API: snake-free camelCase booleans `isPowerPlayEnabled`, `isTickerAutomationEnabled`, etc.
 * Banner/logo files are sent separately as multipart `bannerUri` / `logoUri`.
 */
export function buildUpdateTournamentFields(
  draft: Record<string, unknown>,
  tournamentId: string,
  leagueId: string | null,
) {
  const isLimitedOvers = draft?.matchType === "LIMITED_OVERS";
  const isTest = draft?.matchType === "TEST";
  const matchTypeUi = draft?.matchType != null ? String(draft.matchType) : "LIMITED_OVERS";

  // Settings the form doesn't edit are sent only when the loaded tournament told us their value; otherwise they
  // are left out and the backend keeps what is stored (sending a guess would overwrite it).
  const isAbandoned = readBool(draft, "isAbandonedGameCounted");
  const tickerOn =
    readBool(draft, "isTickerAutomationEnabled", "tickerAutomationEnabled") ?? true;
  const powerOn = readBool(draft, "isPowerPlayEnabled", "enablePowerplay", "powerPlayEnabled");
  const powerOvers = ["maxPowerPlayOvers", "maxPowerplayOvers"].map((k) => readNumber(draft, k, NaN)).find((n) => !Number.isNaN(n));

  const ranking =
    typeof draft.rankingPreference === "string" && draft.rankingPreference.trim()
      ? draft.rankingPreference
      : undefined;
  const gender = typeof draft.gender === "string" && draft.gender.trim() ? draft.gender : undefined;
  const country = typeof draft.country === "string" && draft.country.trim() ? draft.country.trim() : undefined;

  return {
    tournamentId,
    tournamentName: draft?.tournamentName ?? "",
    organizerName: draft?.organizerName ?? "",
    organizerPhoneNumber: toNationalPhoneNumber(draft?.organizerPhoneNumber, draft?.organizerPhoneNumberCountryCode),
    organizerPhoneNumberCountryCode: normalizeCountryCodeForApi(draft?.organizerPhoneNumberCountryCode),
    organizerEmailId: draft?.organizerEmailId ?? "",
    startDate: draft?.startDate ?? "",
    endDate: draft?.endDate ?? "",
    matchType: mapMatchTypeForUpdateApi(matchTypeUi),
    // Multi-day sends overs per innings like the app (90 by default); 0 would make the backend store 450
    maxOvers: isLimitedOvers ? readNumber(draft, "maxOvers", 0) : isTest ? readNumber(draft, "maxOvers", 90) : 0,
    maxOversPerBowler: isTest ? 0 : readNumber(draft, "maxOversPerBowler", 0),
    tournamentCategory: draft?.tournamentCategory ?? "OTHER",
    ballType: mapBallTypeForApi(draft?.ballType as string | null),
    maxWickets: isLimitedOvers ? readNumber(draft, "maxWickets", 0) : 0,
    leagueId: leagueId != null ? String(leagueId) : String(draft?.leagueId ?? ""),
    description: draft?.description ?? "",
    pointForWin: isTest ? readNumber(draft, "pointForOutrightWin", 0) : readNumber(draft, "pointForWin", 0),
    pointForTie: readNumber(draft, "pointForTie", 0),
    pointForAbandoned: readNumber(draft, "pointForAbandoned", 0),
    isAbandonedGameCounted: isAbandoned,
    isRosterLocked: !!draft?.isRosterLocked,
    canEditScoreCard: !!draft?.canEditScoreCard,
    rankingPreference: ranking,
    isImpactPlayerAllowed: !!draft?.isImpactPlayerAllowed,
    isPowerPlayEnabled: powerOn,
    maxPowerPlayOvers: powerOvers,
    isTickerAutomationEnabled: tickerOn,
    bonusPointsEnabled: !!draft?.bonusPointsEnabled,
    isStrictModeEnabled: !!draft?.isStrictModeEnabled,
    removeBannerImage: !!draft?.removeBannerImage,
    removeLogoImage: !!draft?.removeLogoImage,
    // Match days only apply to multi-day tournaments (the backend ignores it for other formats)
    scheduledDays: isTest ? readNumber(draft, "scheduledDays", 0) : undefined,
    gender,
    country,
    waiverRequired: !!draft?.waiverRequired,
    waiverId: draft?.waiverId ?? null,
    tournamentRules: tournamentRulesForApi(draft, isTest),
    ...(isTest ? {
      pointForFirstInningsLead: draft?.pointForFirstInningsLead ?? null,
      pointForFirstInningsLoss: draft?.pointForFirstInningsLoss ?? null,
      pointForOutrightWin: draft?.pointForOutrightWin ?? null,
      bonusPointForWinByInnings: draft?.bonusPointForWinByInnings ?? null,
    } : {}),
  };
}
